import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, RotateCcw, CalendarCheck } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FADE_Y_IN = { opacity: 0, y: 20 };
const FADE_Y_SHOW = { opacity: 1, y: 0 };
const FADE_Y_OUT = { opacity: 0, y: -20 };
const SLIDE_X_IN = { opacity: 0, x: 30 };
const SLIDE_X_SHOW = { opacity: 1, x: 0 };
const SLIDE_X_OUT = { opacity: 0, x: -30 };
const FADE_IN = { opacity: 0 };
const FADE_SHOW = { opacity: 1 };
const SPIN = { rotate: 360 };
const SPIN_T = { repeat: Infinity, duration: 2, ease: "linear" };
const SCALE_IN = { opacity: 0, scale: 0.97 };
const SCALE_SHOW = { opacity: 1, scale: 1 };

export const Signature = () => {
  const { t, lang } = useLanguage();
  const questions = t("signature.questions");

  const [phase, setPhase] = useState("intro"); // intro | quiz | details | loading | result
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);

  const reset = () => {
    setPhase("intro");
    setQIndex(0);
    setAnswers({});
    setName("");
    setEmail("");
    setResult(null);
  };

  const choose = (key, value) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (qIndex < questions.length - 1) {
      setQIndex(qIndex + 1);
    } else {
      setPhase("details");
    }
  };

  const back = () => {
    if (phase === "details") {
      setPhase("quiz");
      setQIndex(questions.length - 1);
    } else if (phase === "quiz" && qIndex > 0) {
      setQIndex(qIndex - 1);
    } else if (phase === "quiz") {
      setPhase("intro");
    }
  };

  const reveal = async () => {
    setPhase("loading");
    try {
      const res = await axios.post(`${API}/signature`, {
        answers,
        lang,
        name: name || null,
        email: email || null,
      });
      if (!res.data.image && !res.data.narrative) {
        throw new Error("empty");
      }
      setResult(res.data);
      setPhase("result");
    } catch (err) {
      toast.error(t("signature.error"));
      setPhase("details");
    }
  };

  const progress = ((qIndex + 1) / questions.length) * 100;

  return (
    <section
      id="signature"
      data-testid="signature-section"
      className="relative py-28 sm:py-40 bg-[#080f18] overflow-hidden"
    >
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/5 blur-3xl pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-6 sm:px-8">
        <div className="text-center mb-14">
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-6 flex items-center justify-center gap-2">
            <Sparkles size={15} /> {t("signature.eyebrow")}
          </p>
          <h2 className="font-serif-lux text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
            {t("signature.title")}
            <span className="gold-gradient-text italic">{t("signature.titleAccent")}</span>
          </h2>
        </div>

        <div className="relative bg-[#0B1622] border border-[#D4AF37]/20 p-8 sm:p-14 min-h-[420px] flex flex-col justify-center">
          <AnimatePresence mode="wait">
            {phase === "intro" && (
              <motion.div
                key="intro"
                initial={FADE_Y_IN}
                animate={FADE_Y_SHOW}
                exit={FADE_Y_OUT}
                className="text-center"
                data-testid="signature-intro"
              >
                <p className="text-[#A0ABC0] font-light text-lg leading-relaxed max-w-xl mx-auto">
                  {t("signature.intro")}
                </p>
                <button
                  onClick={() => setPhase("quiz")}
                  data-testid="signature-start-button"
                  className="mt-10 bg-[#D4AF37] text-[#0A1420] font-semibold hover:bg-[#E6C27A] transition-colors duration-300 px-10 py-4 uppercase tracking-widest text-sm"
                >
                  {t("signature.start")}
                </button>
              </motion.div>
            )}

            {phase === "quiz" && (
              <motion.div
                key={`q-${qIndex}`}
                initial={SLIDE_X_IN}
                animate={SLIDE_X_SHOW}
                exit={SLIDE_X_OUT}
                data-testid="signature-quiz"
              >
                <div className="flex items-center justify-between mb-8">
                  <button
                    onClick={back}
                    className="text-[#A0ABC0] hover:text-[#D4AF37] transition-colors flex items-center gap-2 text-sm"
                    data-testid="signature-back-button"
                  >
                    <ArrowLeft size={16} /> {t("signature.back")}
                  </button>
                  <span className="text-xs uppercase tracking-widest text-[#A0ABC0]">
                    {t("signature.step")} {qIndex + 1} {t("signature.of")} {questions.length}
                  </span>
                </div>
                <div className="h-px bg-white/10 mb-10">
                  <div
                    className="h-px bg-[#D4AF37] transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                <h3 className="font-serif-lux text-2xl sm:text-3xl text-white mb-8 text-center">
                  {questions[qIndex].q}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {questions[qIndex].options.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => choose(questions[qIndex].key, opt.value)}
                      data-testid={`signature-option-${opt.value}`}
                      className="group border border-white/15 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all duration-300 px-6 py-5 text-left"
                    >
                      <span className="text-white group-hover:text-[#D4AF37] transition-colors font-light">
                        {opt.label}
                      </span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {phase === "details" && (
              <motion.div
                key="details"
                initial={FADE_Y_IN}
                animate={FADE_Y_SHOW}
                exit={FADE_Y_OUT}
                className="text-center"
                data-testid="signature-details"
              >
                <button
                  onClick={back}
                  className="text-[#A0ABC0] hover:text-[#D4AF37] transition-colors flex items-center gap-2 text-sm mb-8 mx-auto"
                >
                  <ArrowLeft size={16} /> {t("signature.back")}
                </button>
                <h3 className="font-serif-lux text-3xl text-white">{t("signature.detailsTitle")}</h3>
                <p className="mt-4 text-[#A0ABC0] font-light max-w-md mx-auto">
                  {t("signature.detailsSubtitle")}
                </p>
                <div className="mt-10 max-w-md mx-auto space-y-6">
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("signature.namePlaceholder")}
                    data-testid="signature-name-input"
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 outline-none transition-colors placeholder:text-[#A0ABC0]/60 text-center"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("signature.emailPlaceholder")}
                    data-testid="signature-email-input"
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 outline-none transition-colors placeholder:text-[#A0ABC0]/60 text-center"
                  />
                  <button
                    onClick={reveal}
                    data-testid="signature-reveal-button"
                    className="w-full bg-[#D4AF37] text-[#0A1420] font-semibold hover:bg-[#E6C27A] transition-colors duration-300 px-10 py-4 uppercase tracking-widest text-sm"
                  >
                    {t("signature.reveal")}
                  </button>
                </div>
              </motion.div>
            )}

            {phase === "loading" && (
              <motion.div
                key="loading"
                initial={FADE_IN}
                animate={FADE_SHOW}
                exit={FADE_IN}
                className="text-center py-10"
                data-testid="signature-loading"
              >
                <motion.div
                  animate={SPIN}
                  transition={SPIN_T}
                  className="mx-auto w-14 h-14 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full"
                />
                <p className="mt-8 text-[#D4AF37] font-serif-lux italic text-xl">
                  {t("signature.generating")}
                </p>
              </motion.div>
            )}

            {phase === "result" && result && (
              <motion.div
                key="result"
                initial={SCALE_IN}
                animate={SCALE_SHOW}
                exit={FADE_IN}
                data-testid="signature-result"
              >
                {result.image && (
                  <div className="relative overflow-hidden border border-[#D4AF37]/30 mb-8">
                    <img
                      src={result.image}
                      alt={result.title}
                      data-testid="signature-result-image"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                )}
                <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs text-center mb-3">
                  {t("signature.resultEyebrow")}
                </p>
                <h3 className="font-serif-lux text-3xl sm:text-4xl gold-gradient-text italic text-center">
                  {result.title}
                </h3>
                <p className="mt-6 text-[#A0ABC0] font-light text-lg leading-relaxed text-center max-w-2xl mx-auto">
                  {result.narrative}
                </p>
                <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() =>
                      document.querySelector("#consultation")?.scrollIntoView({ behavior: "smooth" })
                    }
                    data-testid="signature-book-button"
                    className="bg-[#D4AF37] text-[#0A1420] font-semibold hover:bg-[#E6C27A] transition-colors duration-300 px-8 py-4 uppercase tracking-widest text-sm flex items-center justify-center gap-2"
                  >
                    <CalendarCheck size={18} /> {t("signature.bookCta")}
                  </button>
                  <button
                    onClick={reset}
                    data-testid="signature-restart-button"
                    className="border border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37] transition-colors duration-300 px-8 py-4 uppercase tracking-widest text-sm flex items-center justify-center gap-2"
                  >
                    <RotateCcw size={16} /> {t("signature.restart")}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
